import React, { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { UserApiContext } from "../../../../UserDetailsContext/UserDetailsContext";
import {
  downloadStandardFormat,
  readExcelFile,
  trackUserUploadsMastersFileForBulkUploads,
  isValidRouteType,
  isValidSpotRoute
} from "../../Utility/utility";

const RM_HEADERS = [
  "Sr No.",
  "Origin Code",
  "Origin*",
  "Origin District",
  "Origin State*",
  "Origin Pincode",
  "Destination Code",
  "Destination*",
  "Destination District",
  "Destination State*",
  "Destination Pincode",
  "Route Type*",
  "Distance",
  "Duration",
  "Spot Route*",
  "Route Code*",
  "No of hops",
  "Sort Order",
];

const mandatoryHeaders = [
  "Origin*",
  "Origin State*",
  "Destination*",
  "Destination State*",
  "Route Type*",
  "Spot Route*",
  "Route Code*",
  "Duration",
  "Distance",
];

/**
 * Step 1 for upload file in material master
 */
const UploadFile = ({ setStep = "", setUploadedData, company_id }) => {
  const [errors, setErrors] = useState([]);
  const [errorTypes, setErrorTypes] = useState({
    wrongFileType: false,
    headersMismatch: false,
    missingMandatoryFields: false,
    shortRouteCode: false,
    invalidRouteType: false,
    invalidLocationCodeLength: false,
  });

  const fileInputRef = useRef(null);
  const { userDetails } = useContext(UserApiContext);

  /**
   * Function to validate excel data
   * @param {array} data
   * @returns {number}
   */
  const validateData = (data) => {
    const errors = [];
    const newErrorTypes = {};

    if (data.length === 0) {
      toast.info("Please Upload a valid File.");
      return false;
    }

    const headers = Object.keys(data[0] || {});

    const missingHeaders = mandatoryHeaders.filter(
      (header) => !headers.includes(header)
    );

    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
      newErrorTypes.headersMismatch = true;
    }

    for (let i = 0; i < data.length; i++) {
      const row = data[i];

      // check for mandatory fields
      for (const header of mandatoryHeaders) {
        const value = row[header];
        if (!value || value.toString().trim() === "") {
          errors.push(`Row ${i + 1}: ${header} cannot be empty`);
          newErrorTypes.missingMandatoryFields = true;
        }
      }

      // validations for Route Code* and Route Type*
      const routeCode = row["Route Code*"];
      const routeType = row["Route Type*"];
      const originCode = row["Origin Code"];
      const destinationCode = row["Destination Code"];
      const spotRoute = row["Spot Route*"];

      if (routeCode && routeCode.toString().trim().length < 3) {
        errors.push(
          `Row ${i + 1}: Route Code must be at least 3 characters long.`
        );
        newErrorTypes.shortRouteCode = true;
      }

      if (routeType && !isValidRouteType(routeType)) {
        errors.push(
          `Row ${
            i + 1
          }: Route Type must be either Point to Point or Multi Point.`
        );
        newErrorTypes.invalidRouteType = true;
      }

      if (spotRoute && !isValidSpotRoute(spotRoute)) {
        errors.push(`Row ${i + 1}: Spot Route must be either "Yes" or "No".`);
        newErrorTypes.invalidSpotRoute = true;
      }

      // validations for Origin Code and Destination Code
      if (originCode && originCode.toString().trim().length < 3) {
        errors.push(
          `Row ${i + 1}: Origin Code should be at least 3 characters long.`
        );
        newErrorTypes.invalidLocationCodeLength = true;
      }

      if (destinationCode && destinationCode.toString().trim().length < 3) {
        errors.push(
          `Row ${i + 1}: Destination Code should be at least 3 characters long`
        );
        newErrorTypes.invalidLocationCodeLength = true;
      }
    }

    setErrors(errors);
    setErrorTypes(newErrorTypes);
    return errors.length === 0;
  };

  /**
   * Handles the file upload, including validation.
   * @param {Event} e - The file input change event.
   */
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      resetFileAndErrors();

      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrorTypes((prev) => ({ ...prev, wrongFileType: true }));
        setErrors(["The file must be in .xlsx or .xls format."]);
        return;
      }

      const { data, error } = await readExcelFile(selectedFile);

      if (error) {
        toast.error(error);
        return;
      }

      if (validateData(data)) {
        setUploadedData(data);
        setStep(1);
      }

      trackUserUploadsMastersFileForBulkUploads(
        "route",
        userDetails,
        company_id,
        selectedFile.name
      );
    }
  };

  /**
  * Reset file input, errors and error types 
  * @returns {void}
  */
  const resetFileAndErrors = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setErrors([]);
    setErrorTypes({
      wrongFileType: false,
      headersMismatch: false,
      missingMandatoryFields: false,
      shortRouteCode: false,
      invalidRouteType: false,
      invalidLocationCodeLength: false,
    });
  };

  return (
    <div className="card-body overflow-auto" style={{ maxHeight: "460px" }}>
      <div className="mb-4 text-center">
        <button
          className="btn btn-primary"
          onClick={() => downloadStandardFormat(RM_HEADERS, "RouteMaster")}
        >
          Download Standard Format
        </button>
      </div>
      <div className="alert alert-warning" style={{ margin: 0 }}>
        <h5 className="alert-heading under">
          <u>File Requirements</u>:
        </h5>
        <ul>
          <li
            style={{
              color: errorTypes.wrongFileType ? "red" : "inherit",
              fontWeight: errorTypes.wrongFileType ? "bold" : "normal",
            }}
          >
            The file must be in .xlsx or .xls format.
            {errorTypes.wrongFileType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.headersMismatch ? "red" : "inherit",
              fontWeight: errorTypes.headersMismatch ? "bold" : "normal",
            }}
          >
            Ensure that headers match the standard format.
            {errorTypes.headersMismatch && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.missingMandatoryFields ? "red" : "inherit",
              fontWeight: errorTypes.missingMandatoryFields ? "bold" : "normal",
            }}
          >
            Mandatory fields (*):
            {errorTypes.missingMandatoryFields && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
            <ul>
              <li>
                {RM_HEADERS.filter((header) => header.endsWith("*")).map(
                  (header, index, array) => (
                    <React.Fragment key={index}>
                      <span>{header}</span>
                      {index < array.length - 1 && ", "}
                    </React.Fragment>
                  )
                )}
              </li>
            </ul>
          </li>
          <li
            style={{
              color: errorTypes.shortRouteCode ? "red" : "inherit",
              fontWeight: errorTypes.shortRouteCode ? "bold" : "normal",
            }}
          >
            Route Code must be at least 3 characters long.
            {errorTypes.shortRouteCode && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidRouteType ? "red" : "inherit",
              fontWeight: errorTypes.invalidRouteType ? "bold" : "normal",
            }}
          >
            Route Type must be point to point or multi point.
            {errorTypes.invalidRouteType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidSpotRoute ? "red" : "inherit",
              fontWeight: errorTypes.invalidSpotRoute ? "bold" : "normal",
            }}
          >
            Spot Route must be either "Yes" or "No".
            {errorTypes.invalidSpotRoute && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidLocationCodeLength ? "red" : "inherit",
              fontWeight: errorTypes.invalidLocationCodeLength ? "bold" : "normal",
            }}
          >
            Origin Code and Destination Code should be at least 3 characters
            long.
            {errorTypes.invalidLocationCodeLength && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
        </ul>
      </div>
      <div className="text-center">
        <label className="btn btn-success upload-file-button-enquiry-my-account m-4">
          <input
            type="file"
            className="form-control d-none"
            id="upload_file_button"
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            ref={fileInputRef}
          />
          <i className="fas fa-upload mr-2"></i>
          Upload File
        </label>
      </div>
      {errors.length > 0 && (
        <div>
          <h6 className="text-danger">Errors:</h6>
          <ul className="list-group">
            {errors.map((error, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-danger"
              >
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadFile;
